import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getOgShareUrl } from "@/utils/share";
import { usePlausible } from "@/hooks/usePlausible";
import { useAuth } from "@/contexts/AuthContext";
import { customFetch } from "@workspace/api-client-react";

interface SharePanelProps {
  quoteId: number;
  text: string;
  author: string;
  triggerClassName?: string;
  triggerLabel?: string;
  "data-testid"?: string;
}

function buildShareUrls(text: string, author: string, shareUrl: string) {
  const shortText = text.length > 220 ? text.slice(0, 219) + "\u2026" : text;
  const tweetText = encodeURIComponent(`\u201C${shortText}\u201D \u2014 ${author}`);
  const waText = encodeURIComponent(`\u201C${shortText}\u201D \u2014 ${author}\n\n${shareUrl}`);
  const encodedUrl = encodeURIComponent(shareUrl);

  return {
    twitter: `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodedUrl}&hashtags=QuotedsArrow`,
    whatsapp: `https://wa.me/?text=${waText}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };
}

const rowBase =
  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-100 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400";

function PlatformIcon({ platform }: { platform: string }) {
  switch (platform) {
    case "twitter":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-current text-stone-800" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "whatsapp":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
          <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      );
    case "facebook":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
          <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
          <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    default:
      return null;
  }
}

export function SharePanel({
  quoteId,
  text,
  author,
  triggerClassName,
  triggerLabel = "↗ Share",
  "data-testid": testId,
}: SharePanelProps) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const { trackEvent } = usePlausible();
  const { currentUser } = useAuth();

  const referralQuery = useQuery({
    queryKey: ["/api/profile/referral-code"],
    queryFn: () =>
      customFetch<{ code: string; referralUrl: string; referralCount: number }>(
        "/api/profile/referral-code",
        { method: "GET" },
      ),
    enabled: !!currentUser,
    staleTime: Infinity,
  });
  const referralCode = referralQuery.data?.code ?? null;

  const shareUrl = getOgShareUrl(quoteId, referralCode);
  const urls = buildShareUrls(text, author, shareUrl);
  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  function openPlatform(url: string) {
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
    setOpen(false);
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setOpen(false);
    }, 1500);
  }

  async function handleNativeShare() {
    const shortText = text.length > 220 ? text.slice(0, 219) + "\u2026" : text;
    try {
      await navigator.share({
        title: `\u201C${shortText}\u201D \u2014 ${author}`,
        text: `\u201C${shortText}\u201D \u2014 ${author}`,
        url: shareUrl,
      });
    } catch {
      // user cancelled
    }
    setOpen(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) trackEvent("Quote Shared");
    setOpen(nextOpen);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={triggerClassName}
          data-testid={testId}
          title="Share this quote"
          onClick={(e) => e.stopPropagation()}
        >
          {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-52 p-2"
        align="end"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-stone-400">
          Share to
        </p>

        <button onClick={() => openPlatform(urls.twitter)} className={rowBase}>
          <PlatformIcon platform="twitter" />
          X / Twitter
        </button>

        <button onClick={() => openPlatform(urls.whatsapp)} className={rowBase}>
          <PlatformIcon platform="whatsapp" />
          WhatsApp
        </button>

        <button onClick={() => openPlatform(urls.facebook)} className={rowBase}>
          <PlatformIcon platform="facebook" />
          Facebook
        </button>

        <button onClick={() => openPlatform(urls.linkedin)} className={rowBase}>
          <PlatformIcon platform="linkedin" />
          LinkedIn
        </button>

        <div className="my-1.5 border-t border-stone-100" />

        <button onClick={handleCopy} className={rowBase}>
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-stone-500">
            {copied ? "✓" : "⎘"}
          </span>
          {copied ? "Copied!" : "Copy link"}
        </button>

        {hasNativeShare && (
          <button onClick={handleNativeShare} className={rowBase}>
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-stone-500">
              ↗
            </span>
            More options…
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
